import { usePageTitle } from "@/hooks/use-page-title";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import {
  Inbox,
  Building2,
  HelpCircle,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  DollarSign,
  CreditCard,
  ShieldCheck,
  ExternalLink,
  Mail,
  Phone,
  ChevronRight,
} from "lucide-react";

function formatCents(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}

function PaymentStatusBadge({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return (
        <Badge variant="default" className="gap-1" data-testid={`badge-status-${status}`}>
          <CheckCircle2 className="w-3 h-3" />
          Paid
        </Badge>
      );
    case "approved":
    case "processing":
      return (
        <Badge variant="secondary" className="gap-1" data-testid={`badge-status-${status}`}>
          <Clock className="w-3 h-3" />
          Pending
        </Badge>
      );
    case "pending":
      return (
        <Badge variant="outline" className="gap-1" data-testid={`badge-status-${status}`}>
          <AlertCircle className="w-3 h-3" />
          Awaiting Approval
        </Badge>
      );
    case "rejected":
    case "failed":
      return (
        <Badge variant="destructive" className="gap-1" data-testid={`badge-status-${status}`}>
          <XCircle className="w-3 h-3" />
          {status === "rejected" ? "Rejected" : "Failed"}
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function PaymentInboxTab() {
  const { toast } = useToast();

  const { data: payments = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/partner/payments"],
  });

  const markReceivedMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const res = await apiRequest("PATCH", `/api/partner/payments/${paymentId}/received`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/partner/payments"] });
      toast({ title: "Payment confirmed", description: "This payment has been marked as received." });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message || "Failed to mark payment", variant: "destructive" });
    },
  });

  const canMarkReceived = (status: string) =>
    ["approved", "processing", "completed"].includes(status);

  if (isLoading) {
    return (
      <div className="space-y-3 p-1">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center" data-testid="empty-payments">
        <Inbox className="h-10 w-10 text-muted-foreground" />
        <p className="font-medium">No payments yet</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Payments from patient wallets will appear here once approved by the platform.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden" data-testid="payments-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Invoice</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((payment: any) => (
            <TableRow key={payment.id} data-testid={`row-payment-${payment.id}`}>
              <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                {payment.requestedAt
                  ? format(new Date(payment.requestedAt), "MMM d, yyyy")
                  : "—"}
              </TableCell>
              <TableCell>
                <p className="text-sm font-medium">
                  {payment.serviceDescription || "Healthcare services"}
                </p>
                {payment.serviceDate && (
                  <p className="text-xs text-muted-foreground">
                    Service: {format(new Date(payment.serviceDate), "MMM d, yyyy")}
                  </p>
                )}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {payment.invoiceNumber || "—"}
              </TableCell>
              <TableCell className="text-right font-medium">
                {formatCents(payment.amount)}
              </TableCell>
              <TableCell>
                <PaymentStatusBadge status={payment.status} />
              </TableCell>
              <TableCell>
                {canMarkReceived(payment.status) && payment.status !== "completed" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markReceivedMutation.mutate(payment.id)}
                    disabled={markReceivedMutation.isPending}
                    data-testid={`button-mark-received-${payment.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    Mark Received
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AccountTab() {
  const { data: account, isLoading } = useQuery<any>({
    queryKey: ["/api/partner/account"],
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <Building2 className="h-10 w-10 text-muted-foreground" />
        <p className="font-medium">No partner account linked</p>
        <p className="text-sm text-muted-foreground">
          Contact support to link your organization.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card data-testid="card-org-info">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" />
            Organization
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-1">Organization Name</p>
              <p className="font-medium" data-testid="text-org-name">{account.partner.name}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Partner Code</p>
              <p className="font-medium font-mono" data-testid="text-partner-code">{account.partner.partnerCode}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Account Status</p>
              <Badge
                variant={account.partner.status === "active" ? "default" : "secondary"}
                data-testid="badge-partner-status"
              >
                {account.partner.status}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {account.provider && (
        <Card data-testid="card-provider-info">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4" />
              Payment Details
            </CardTitle>
            <CardDescription>
              Financial information on file with HealthWallet
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs mb-1">Provider Name</p>
                <p className="font-medium" data-testid="text-provider-name">{account.provider.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Provider Type</p>
                <p className="font-medium capitalize">{account.provider.providerType?.replace(/_/g, " ") || "—"}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">EIN</p>
                <p className="font-mono font-medium" data-testid="text-ein-masked">
                  {account.provider.einMasked || "Not on file"}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs mb-1">Payout Method</p>
                <p className="font-medium capitalize">{account.provider.paymentMethod || "Check"}</p>
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-muted-foreground text-xs mb-1">Verification</p>
              <Badge
                variant={account.provider.verificationStatus === "verified" ? "default" : "secondary"}
                data-testid="badge-verification-status"
              >
                <ShieldCheck className="w-3 h-3 mr-1" />
                {account.provider.verificationStatus === "verified" ? "Verified" : "Pending verification"}
              </Badge>
            </div>

            <Separator />

            <div className="space-y-2">
              <p className="text-sm font-medium">Stripe Connect — Payout Onboarding</p>
              <p className="text-xs text-muted-foreground">
                Connect your bank account via Stripe to receive faster electronic payouts directly to your account.
              </p>
              <Button
                variant="outline"
                className="gap-2"
                data-testid="button-stripe-connect"
                onClick={() => {
                  window.open("https://connect.stripe.com/setup/e/acct_link", "_blank", "noopener");
                }}
              >
                <CreditCard className="h-4 w-4" />
                Set Up Stripe Payouts
                <ExternalLink className="h-3.5 w-3.5 ml-1 text-muted-foreground" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SupportTab() {
  const faqs = [
    {
      q: "When will I receive payment for a service?",
      a: "Payments are disbursed once the patient's wallet has sufficient funds and the request is approved by the platform, typically within 3–5 business days.",
    },
    {
      q: "How do I update my banking information?",
      a: "Contact our partner support team with your updated ACH or bank details. For faster payouts, use the Stripe Connect onboarding in the Account tab.",
    },
    {
      q: "What does 'Awaiting Approval' mean?",
      a: "The payment request has been received by HealthWallet and is under review. You'll be notified once it is approved or if additional information is needed.",
    },
    {
      q: "How do I dispute a rejected payment?",
      a: "Email partner-support@healthwallet.com with your invoice number and a brief description. Our team will review it within 2 business days.",
    },
    {
      q: "Can I submit a payment request directly?",
      a: "Yes — contact your HealthWallet account manager or email partner-support@healthwallet.com to initiate a payment request for services rendered.",
    },
  ];

  return (
    <div className="space-y-6">
      <Card data-testid="card-contact">
        <CardHeader>
          <CardTitle className="text-base">Contact Partner Support</CardTitle>
          <CardDescription>Our team is available Monday–Friday, 8 AM–6 PM CT</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <a
            href="mailto:partner-support@healthwallet.com"
            className="flex items-center gap-3 p-3 rounded-lg border hover-elevate text-sm"
            data-testid="link-email-support"
          >
            <div className="p-2 rounded-full bg-primary/10 text-primary">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium">Email Support</p>
              <p className="text-muted-foreground text-xs">partner-support@healthwallet.com</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
          </a>
          <a
            href="tel:+18005551234"
            className="flex items-center gap-3 p-3 rounded-lg border hover-elevate text-sm"
            data-testid="link-phone-support"
          >
            <div className="p-2 rounded-full bg-primary/10 text-primary">
              <Phone className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium">Call Us</p>
              <p className="text-muted-foreground text-xs">1-800-555-1234</p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
          </a>
        </CardContent>
      </Card>

      <Card data-testid="card-faq">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            Frequently Asked Questions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {faqs.map((item, i) => (
              <div key={i} data-testid={`faq-item-${i}`}>
                <p className="text-sm font-medium">{item.q}</p>
                <p className="text-sm text-muted-foreground mt-1">{item.a}</p>
                {i < faqs.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PartnerPortal() {
  usePageTitle("Partner Portal");
  const { user } = useAuth();

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold" data-testid="heading-partner-portal">
          Partner Portal
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage incoming payments and your organization account.
        </p>
      </div>

      <Tabs defaultValue="inbox" className="space-y-4">
        <TabsList data-testid="tabs-partner-portal">
          <TabsTrigger value="inbox" data-testid="tab-payment-inbox" className="gap-2">
            <Inbox className="h-4 w-4" />
            Payment Inbox
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account" className="gap-2">
            <Building2 className="h-4 w-4" />
            Account
          </TabsTrigger>
          <TabsTrigger value="support" data-testid="tab-support" className="gap-2">
            <HelpCircle className="h-4 w-4" />
            Support
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Inbox className="h-4 w-4" />
                Payment Inbox
              </CardTitle>
              <CardDescription>
                Incoming payments from patient wallets. Mark payments as received once funds arrive.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PaymentInboxTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account">
          <AccountTab />
        </TabsContent>

        <TabsContent value="support">
          <SupportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
